import { Check, ChevronLeft } from 'lucide-react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cn } from '@/shared/utils/cn';
import type { ListingFilter, ListingFilterItem } from '@/features/plp/domain/models/ProductListing';

function categoryHintLine(items: ListingFilterItem[], selectedLabels: string[]): string | null {
  if (selectedLabels.length > 0) return null;
  if (items.length === 0) return null;
  const sample = items
    .slice(0, 4)
    .map((i) => i.displayName)
    .join(', ');
  if (!sample) return null;
  const prefix = 'E.g. ';
  const max = 72;
  const text = sample.length > max - prefix.length ? `${sample.slice(0, max - prefix.length - 1)}…` : sample;
  return `${prefix}${text}`;
}

export interface RecommendedBrowseFiltersPanelProps {
  filters: ListingFilter[];
  selectedOptions: Record<string, string[]>;
  applyUrl: string | null;
  isLoadingPreview: boolean;
  /** Desktop: true while 2s commit timer is running (PLP desktop parity). */
  isPendingCommit?: boolean;
  filtersResetUrl?: string;
  onToggleOption: (category: string, name: string, url: string) => void;
  onApply: () => void;
  onReset: () => void;
  /** When false, hide the Apply footer (desktop auto-commit). */
  showApplyButton?: boolean;
  /** Root wrapper classes (e.g. max height for dropdown vs sheet). */
  className?: string;
  /** Mobile sheet: back control in header; omit on desktop popover. */
  onClose?: () => void;
}

export function RecommendedBrowseFiltersPanel({
  filters,
  selectedOptions,
  applyUrl,
  isLoadingPreview,
  isPendingCommit = false,
  filtersResetUrl,
  onToggleOption,
  onApply,
  onReset,
  showApplyButton = true,
  className,
  onClose,
}: RecommendedBrowseFiltersPanelProps) {
  const totalActiveCount = Object.values(selectedOptions).reduce(
    (sum, names) => sum + names.length,
    0
  );
  const canClear = totalActiveCount > 0 || Boolean(filtersResetUrl);

  return (
    <div className={cn('flex min-h-0 flex-col bg-white', className)}>
      <div
        className="flex shrink-0 items-center justify-between gap-2 px-4 py-3.5"
        style={{ backgroundColor: '#0c2a4a' }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-1">
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 -ml-1 rounded-full p-2 text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              aria-label="Back"
            >
              <ChevronLeft className="h-6 w-6" strokeWidth={2} aria-hidden />
            </button>
          ) : null}
          <h2 className="min-w-0 truncate text-left text-[17px] font-semibold text-white">
            Filters{totalActiveCount > 0 ? ` (${totalActiveCount})` : ''}
            {isLoadingPreview || isPendingCommit ? '…' : ''}
          </h2>
        </div>
        <button
          type="button"
          disabled={!canClear}
          className={cn(
            'text-[13px] font-medium',
            canClear ? 'text-sky-200 hover:text-white' : 'text-white/35 cursor-not-allowed'
          )}
          onClick={() => {
            if (canClear) onReset();
          }}
        >
          Reset
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-white">
        <AccordionPrimitive.Root type="multiple" defaultValue={[]}>
          {filters.map((filter) => {
            const selectedInCategory = selectedOptions[filter.category] ?? [];
            const hint = categoryHintLine(filter.items, selectedInCategory);

            return (
              <AccordionPrimitive.Item
                key={filter.category}
                value={filter.category}
                className="border-b border-gray-200"
              >
                <AccordionPrimitive.Header>
                  <AccordionPrimitive.Trigger className="group flex w-full items-center justify-between px-4 py-4 text-left">
                    <div className="min-w-0 pr-4">
                      <p className="text-[15px] font-bold text-gray-900">{filter.category}</p>
                      {selectedInCategory.length > 0 ? (
                        <p className="mt-0.5 line-clamp-2 text-[13px] text-gray-500">
                          {selectedInCategory.join(', ')}
                        </p>
                      ) : hint ? (
                        <p className="mt-0.5 line-clamp-2 text-[13px] text-gray-400">{hint}</p>
                      ) : null}
                    </div>
                    <span
                      aria-hidden
                      className="shrink-0 text-[22px] leading-none text-gray-500 group-data-[state=open]:hidden"
                    >
                      +
                    </span>
                    <span
                      aria-hidden
                      className="shrink-0 text-[22px] leading-none text-gray-500 group-data-[state=closed]:hidden"
                    >
                      −
                    </span>
                  </AccordionPrimitive.Trigger>
                </AccordionPrimitive.Header>
                <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                  <ul className="px-4 pb-2">
                    {filter.items.map((item) => {
                      const isSelected = selectedInCategory.includes(item.name);
                      return (
                        <li key={item.name} className="border-b border-gray-100 last:border-0">
                          <button
                            type="button"
                            className="flex w-full items-center gap-3 py-3 text-left"
                            onClick={() => onToggleOption(filter.category, item.name, item.url)}
                          >
                            <div
                              className={cn(
                                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2',
                                isSelected
                                  ? 'border-[#3F84AD] bg-[#3F84AD]'
                                  : 'border-[#3F84AD] bg-white'
                              )}
                            >
                              {isSelected && (
                                <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                              )}
                            </div>
                            <span
                              className={cn(
                                'text-[14px]',
                                isSelected ? 'font-bold text-gray-900' : 'text-gray-700'
                              )}
                            >
                              {item.displayName}
                              {item.count > 0 && (
                                <span className="font-normal"> ({item.count})</span>
                              )}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </AccordionPrimitive.Content>
              </AccordionPrimitive.Item>
            );
          })}
        </AccordionPrimitive.Root>
      </div>

      {showApplyButton ? (
        <div className="shrink-0 border-t border-gray-200 bg-white">
          <button
            type="button"
            className="w-full py-4 text-[15px] font-semibold uppercase tracking-widest text-white disabled:opacity-50"
            style={{ backgroundColor: '#0c2a4a' }}
            disabled={!applyUrl}
            onClick={onApply}
          >
            Apply
          </button>
        </div>
      ) : null}
    </div>
  );
}
